import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, BarChart3, Link2, Plus, Copy, Check, ExternalLink, Trash2, RefreshCw, Users, Building2, FolderTree, CreditCard, ArrowLeft, Download, Radio, Globe, Smartphone, Target, Eye, TrendingUp, AlertTriangle, Landmark, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Layout from '../components/Layout.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useTier } from '../hooks/useTier.js';
import { RequiresTier } from '../components/RequiresTier.js';
import { UpgradePrompt } from '../components/UpgradePrompt.js';
import { analyticsRangeFeature, FEATURE_REQUIREMENTS, TIER_PRICING, type Tier } from '@vcard/shared';

// ─── Types ────────────────────────────────────────────────────────────────────
interface StatsSummary {
  totalViews: number;
  uniqueSessions: number;
  uniqueCards: number;
}
interface DailyPoint { day: string; views: number; unique: number; }
interface CountryPoint { country: string | null; views: number; }
interface DevicePoint { device: string | null; views: number; }
interface ReferrerPoint { domain: string | null; views: number; }
interface TopCard { cardId: string; shortCode: string | null; userId: string | null; views: number; unique: number; saves?: number; interactions?: number; }
interface EventRow { type: string; count: number; sessions: number }
interface EventTarget { type: string; target: string; count: number }
interface TopLink { linkId: string; label: string | null; slug: string | null; views: number; }
interface HeatmapPoint { dow: number; hour: number; views: number; }
interface Stats {
  summary: StatsSummary;
  daily: DailyPoint[];
  countries: CountryPoint[];
  devices: DevicePoint[];
  referrers: ReferrerPoint[];
  topCards: TopCard[];
  topLinks: TopLink[];
  heatmap: HeatmapPoint[];
  events?: { byType: EventRow[]; targets: EventTarget[] };
}
interface Sublink {
  id: string;
  slug: string;
  label: string;
  created_at: string;
  views: string;
}

type Scope =
  | { kind: 'card';      id: string | null; label: string }
  | { kind: 'user';      id: string | null; label: string }
  | { kind: 'department'; id: string | null; label: string }
  | { kind: 'organisation'; id: string | null; label: string };

// ─── Public URL helper ────────────────────────────────────────────────────────
function publicBaseUrl(): string {
  // Where card viewers actually come from. Web app is hosted on a different
  // domain than the dashboard; we look it up from the env or fall back to the
  // current origin. In dev, current origin is fine.
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  const fromEnv = env?.['VITE_PUBLIC_APP_URL'];
  return fromEnv || window.location.origin;
}

function sublinkUrl(slug: string): string {
  return `${publicBaseUrl()}/t/${slug}`;
}

// ─── Page ────────────────────────────────────────────────────────────────────
function AnalyticsPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasFeature: tierHas, loading: tierLoading } = useTier();

  const [scope, setScope] = useState<Scope | null>(null);
  const [range, setRange] = useState<'24h' | '7d' | '30d' | '90d' | 'all'>('30d');
  const [rangeLockedHint, setRangeLockedHint] = useState<Tier | null>(null);
  const rangeTouched = useRef(false); // true once the user picked a range themselves

  // Longer time ranges are part of the paid plans (free: 24h and 7d)
  const isRangeAllowed = useCallback((r: '24h' | '7d' | '30d' | '90d' | 'all') => {
    const feature = analyticsRangeFeature(r);
    return !feature || tierHas(feature);
  }, [tierHas]);

  // start on a range the plan includes
  useEffect(() => {
    if (!tierLoading && !isRangeAllowed(range)) setRange('7d');
  }, [tierLoading, isRangeAllowed, range]);
  const [deepLinkApplied, setDeepLinkApplied] = useState(false);
  const [live, setLive] = useState<number>(0);
  const [stats, setStats] = useState<Stats | null>(null);
  const [links, setLinks] = useState<Sublink[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backendUp, setBackendUp] = useState<boolean | null>(null);

  // ── Available scopes (org → departments → users → cards) ───────────────
  const [orgs, setOrgs]   = useState<{ id: string; name: string; slug: string }[]>([]);
  const [depts, setDepts] = useState<{ id: string; name: string; organisation_id: string }[]>([]);
  const [users, setUsers] = useState<{ id: string; email: string; organisation_id: string }[]>([]);
  const [cards, setCards] = useState<{ id: string; slug: string; user_id: string; short_code: string | null; data: { name?: string; firstName?: string; lastName?: string } }[]>([]);

  useEffect(() => {
    if (!user) return;
    const headers = { credentials: 'include' as RequestCredentials };

    // Cards: always available, role-scoped by the endpoint
    fetch('/api/admin/cards', headers).then(r => r.json()).then((d: unknown) => {
      const rows = Array.isArray(d) ? d as Array<{
        id?: string | null;
        userId?: string;
        user_id?: string;
        shortCode?: string | null;
        short_code?: string | null;
        name?: string;
        slug?: string;
        data?: { name?: string; firstName?: string; lastName?: string };
      }> : [];
      const normalised = rows.map((row) => ({
        id: row.id ?? row.slug ?? '',
        slug: row.slug ?? '',
        user_id: row.userId ?? row.user_id ?? '',
        short_code: row.shortCode ?? row.short_code ?? null,
        data: row.data ?? (row.name !== undefined ? { name: row.name } : {}),
      })).filter(c => c.id);
      setCards(normalised);
    }).catch(() => undefined);

    // Departments: visible to admin/owner/department_lead
    if (['superadmin', 'owner', 'admin', 'department_lead'].includes(user.role)) {
      fetch('/api/admin/departments', headers).then(r => r.ok ? r.json() : []).then((d: unknown) => {
        const list = Array.isArray(d) ? d : ((d as { departments?: unknown[] })?.departments ?? []);
        const rows = list as Array<{ id: string; name: string; organisation_id?: string; organisationId?: string }>;
        setDepts(rows.map((r) => ({ id: r.id, name: r.name, organisation_id: r.organisation_id ?? r.organisationId ?? '' })));
      }).catch(() => undefined);
    }

    // Org list: only for superadmin
    if (user.role === 'superadmin') {
      fetch('/api/admin/organisations', headers).then(r => r.ok ? r.json() : []).then((d: unknown) => {
        const list = Array.isArray(d)
          ? d as Array<{ id: string; name: string; slug: string }>
          : ((d as { organisations?: { id: string; name: string; slug: string }[] })?.organisations ?? []);
        setOrgs(list);
      }).catch(() => undefined);
    } else {
      // Implicitly the current user's org
      setOrgs([{ id: user.organisationId, name: 'Your Organisation', slug: '' }]);
    }

    // User list: visible to admin/owner/department_lead
    if (['superadmin', 'owner', 'admin', 'department_lead'].includes(user.role)) {
      fetch('/api/admin/users', headers).then(r => r.ok ? r.json() : []).then((d: unknown) => {
        const list = Array.isArray(d) ? d : ((d as { users?: unknown[] })?.users ?? []);
        const rows = list as Array<{ id: string; email: string; organisation_id?: string; organisationId?: string }>;
        setUsers(rows.map((r) => ({ id: r.id, email: r.email, organisation_id: r.organisation_id ?? r.organisationId ?? '' })));
      }).catch(() => undefined);
    } else {
      setUsers([{ id: user.id, email: user.email, organisation_id: user.organisationId }]);
    }
  }, [user]);

  // Pick a sensible default scope once data lands
  useEffect(() => {
    if (scope || !user) return;

    // Deep-link from /cards → /analytics?card=...&userId=...
    const params = new URLSearchParams(window.location.search);
    const deepCardId = params.get('card');
    if (deepCardId && cards.length) {
      const c = cards.find(x => x.id === deepCardId);
      if (c) {
        setScope({ kind: 'card', id: c.id!, label: cardLabel(c) });
        setDeepLinkApplied(true);
        return;
      }
    }
    const deepUserId = params.get('userId');
    if (deepUserId) {
      setScope({ kind: 'user', id: deepUserId, label: users.find(u => u.id === deepUserId)?.email ?? 'User' });
      setDeepLinkApplied(true);
      return;
    }
    if (deepLinkApplied) return;

    // Default: land on Organisation scope so the user sees their org's
    // overview immediately. They can then drill down via the tabs.
    if (user.role === 'superadmin') {
      if (orgs.length === 1) {
        setScope({ kind: 'organisation', id: orgs[0]!.id, label: orgs[0]!.name });
      } else {
        setScope({ kind: 'organisation', id: null, label: t('analytics.chooseOrg', 'Choose organisation') });
      }
      return;
    }
    // owner / admin / member / department_lead: their own org
    setScope({ kind: 'organisation', id: user.organisationId, label: t('analytics.yourOrg', 'Your organisation') });
  }, [user, scope, orgs, cards, users, deepLinkApplied, t]);

  // Fetch stats + links when scope / range changes (with auto-retry on 502/5xx)
  const refresh = useCallback(async () => {
    if (!scope) return;
    if (scope.kind !== 'organisation' && !scope.id) {
      setStats(null);
      setLinks(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setBackendUp(null);
    const maxAttempts = 3;
    let lastErr: string = '';
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Quick health probe first so we can tell the user "server is down"
        // vs "server up but this specific request errored"
        if (attempt === 1) {
          try {
            const hp = await fetch('/api/health', { credentials: 'include' });
            setBackendUp(hp.ok);
            if (!hp.ok) {
              lastErr = t('analytics.backendDown', 'Backend server is not responding. Is docker compose up?');
              await new Promise((r) => setTimeout(r, 600 * attempt));
              continue;
            }
          } catch {
            setBackendUp(false);
            lastErr = t('analytics.backendUnreachable', 'Backend server unreachable. Check that the API container is running.');
            await new Promise((r) => setTimeout(r, 600 * attempt));
            continue;
          }
        }

        let statsUrl = '';
        if (scope.kind === 'card') {
          statsUrl = `/api/analytics/card/${scope.id}?range=${range}`;
        } else if (scope.kind === 'user') {
          statsUrl = `/api/analytics/user/${scope.id}?range=${range}`;
        } else if (scope.kind === 'department') {
          statsUrl = `/api/analytics/department/${scope.id}?range=${range}`;
        } else {
          statsUrl = `/api/analytics/organisation?range=${range}` + (scope.id ? `&orgId=${scope.id}` : '');
        }
        const statsRes = await fetch(statsUrl, { credentials: 'include' });
        if (!statsRes.ok) {
          const body = await statsRes.json().catch(() => ({} as { error?: string }));
          if (statsRes.status === 403) {
            throw new Error(t('analytics.forbidden', 'You do not have access to this view.'));
          }
          if (statsRes.status === 402) {
            // plan does not include this range: fall back and tell the user
            if (rangeTouched.current) {
              const feature = analyticsRangeFeature(range);
              setRangeLockedHint((feature ? FEATURE_REQUIREMENTS[feature] : 'starter') as Tier);
            }
            setRange('7d');
            setLoading(false);
            return;
          }
          if (statsRes.status === 502 || statsRes.status === 503 || statsRes.status === 504) {
            // transient — retry
            lastErr = t('analytics.upstream', 'Analytics service temporarily unavailable — try again in a moment.');
            await new Promise((r) => setTimeout(r, 600 * attempt));
            continue;
          }
          throw new Error((body as { error?: string }).error || `HTTP ${statsRes.status}`);
        }
        const statsData = await statsRes.json() as { stats: Stats };
        setStats(statsData.stats);

        if (scope.kind === 'card' && scope.id) {
          const linksRes = await fetch(`/api/analytics/links/${scope.id}`, { credentials: 'include' });
          if (linksRes.ok) setLinks(await linksRes.json() as Sublink[]);
          else setLinks([]);
        } else {
          setLinks(null);
        }
        setError(null);
        setLoading(false);
        return; // success
      } catch (err) {
        lastErr = err instanceof Error ? err.message : String(err);
        // Network-level error (server crashed mid-request) → retry
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, 600 * attempt));
          continue;
        }
      }
    }
    setError(lastErr);
    setLoading(false);
  }, [scope, range, t]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Live counter — only meaningful in card scope, polls every 15s
  useEffect(() => {
    if (scope?.kind !== 'card' || !scope.id) { setLive(0); return; }
    const cardId = scope.id;
    let active = true;
    const tick = async () => {
      try {
        const r = await fetch(`/api/analytics/live/${cardId}`, { credentials: 'include' });
        if (active && r.ok) {
          const data = await r.json() as { live: number };
          setLive(data.live);
        }
      } catch { /* ignore */ }
    };
    void tick();
    const h = setInterval(tick, 15_000);
    return () => { active = false; clearInterval(h); };
  }, [scope]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Layout title={t('analytics.pageTitle', 'Analytics')}>
      <div className="max-w-7xl space-y-6">
        {rangeLockedHint && (
          <UpgradePrompt
            compact
            requiredTier={rangeLockedHint}
            message={t('paywall.rangeLocked', 'Dieser Zeitraum ist in deinem Plan nicht enthalten.')}
          />
        )}
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm text-ink-muted">
                {t('analytics.subtitle', 'See who opens your cards, when, from where.')}
              </p>
            </div>
            {scope?.kind === 'card' && scope.id && (
              <LiveBadge count={live} />
            )}
          </div>
          <div className="flex items-center gap-2">
            {scope?.kind === 'card' && scope.id && (
              <a
                href={`/api/analytics/card/${scope.id}/export.csv?range=${range}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink hover:bg-raised transition"
              >
                <Download className="h-4 w-4" />
                {t('analytics.exportCsv', 'Export CSV')}
              </a>
            )}
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center gap-2 rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink hover:bg-raised transition"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {t('analytics.refresh', 'Refresh')}
            </button>
          </div>
        </header>

        <ScopeBar
          scope={scope}
          onScopeChange={setScope}
          range={range}
          onRangeChange={(r) => { rangeTouched.current = true; setRangeLockedHint(null); setRange(r); }}
          isRangeAllowed={isRangeAllowed}
          onLockedRange={(r) => {
            const feature = analyticsRangeFeature(r);
            setRangeLockedHint((feature ? FEATURE_REQUIREMENTS[feature] : 'starter') as Tier);
          }}
          orgs={orgs}
          depts={depts}
          users={users}
          cards={cards}
          currentUserId={user?.id}
        />

        {error && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-danger-300 dark:border-danger-500/40 bg-danger-50 dark:bg-danger-500/10 px-4 py-3 text-sm text-danger-700 dark:text-danger-200">
            <div>
              <div className="font-semibold">{t('analytics.error', 'Could not load analytics')}</div>
              <div className="text-xs opacity-80 mt-0.5">{error}</div>
            </div>
            <button
              type="button"
              onClick={refresh}
              className="shrink-0 rounded-md border border-danger-300/60 dark:border-danger-500/30 bg-raised px-3 py-1.5 text-xs font-medium hover:bg-line"
            >
              {t('analytics.retry', 'Retry')}
            </button>
          </div>
        )}

        {/* Backend-Status-Hint: prominent wenn down */}
        {backendUp === false && !loading && (
          <div className="rounded-lg border border-warning-300 dark:border-warning-500/40 bg-warning-50 dark:bg-warning-500/10 px-4 py-3 text-sm text-warning-800 dark:text-warning-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">{t('analytics.backendDownTitle', 'API server is not responding')}</div>
                <div className="text-xs opacity-80 mt-1">{t('analytics.backendDownHint', 'Make sure the API container is running: `docker compose up -d` and `pnpm dev` in packages/api.')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Browser: shown when a non-org scope is selected but no id is set yet */}
        {scope && !scope.id && !error ? (
          <ScopeBrowser
            scope={scope}
            orgs={orgs}
            depts={depts}
            users={users}
            cards={cards}
            onPick={(s) => setScope(s)}
          />
        ) : loading && !stats ? (
          <SkeletonGrid />
        ) : stats ? (
          <>
            <SummaryCards stats={stats} />
            <DailyChart daily={stats.daily} />
            <InteractionsPanel stats={stats} />
            <Heatmap points={stats.heatmap} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TopList
                title={t('analytics.topCountries', 'Top countries')}
                icon={Globe}
                empty={t('analytics.noCountry', 'No geo data yet')}
                items={stats.countries.map(c => ({
                  key: c.country ?? 'unknown',
                  label: countryName(c.country),
                  value: c.views,
                }))}
              />
              <TopList
                title={t('analytics.topDevices', 'Devices')}
                icon={Smartphone}
                empty={t('analytics.noDevice', 'No device data yet')}
                items={stats.devices.map(d => ({
                  key: d.device ?? 'unknown',
                  label: d.device ?? 'unknown',
                  value: d.views,
                }))}
              />
              <TopList
                title={t('analytics.topReferrers', 'Top referrers')}
                icon={Link2}
                empty={t('analytics.noReferrer', 'Direct traffic only')}
                items={stats.referrers.map(r => ({
                  key: r.domain ?? 'direct',
                  label: r.domain ?? 'Direct',
                  value: r.views,
                }))}
              />
              {stats.topLinks.some(l => l.linkId) && (
                <TopList
                  title={t('analytics.topLinks', 'Top tracking links')}
                  icon={Target}
                  empty=""
                  items={stats.topLinks.filter(l => l.linkId).map(l => ({
                    key: l.linkId,
                    label: l.label ?? l.slug ?? l.linkId.slice(0, 6),
                    value: l.views,
                  }))}
                />
              )}
            </div>

            {scope?.kind === 'card' && scope.id && (
              <RequiresTier
                feature="tracking_links"
                fallback={
                  <UpgradePrompt
                    requiredTier={FEATURE_REQUIREMENTS['tracking_links'] as Tier}
                    message={t('paywall.trackingLinks', 'Tracking-Links messen einzelne Kanäle (Messe, LinkedIn …) und sind ab dem Plan {{tier}} verfügbar.', { tier: TIER_PRICING[FEATURE_REQUIREMENTS['tracking_links'] as Tier].label })}
                  />
                }
              >
                <SublinkPanel
                  cardId={scope.id}
                  links={links ?? []}
                  onChange={refresh}
                />
              </RequiresTier>
            )}
          </>
        ) : null}
      </div>
    </Layout>
  );
}

// ─── Scope selector ──────────────────────────────────────────────────────────
function ScopeBar(props: {
  scope: Scope | null;
  onScopeChange: (s: Scope) => void;
  range: '24h' | '7d' | '30d' | '90d' | 'all';
  onRangeChange: (r: '24h' | '7d' | '30d' | '90d' | 'all') => void;
  isRangeAllowed: (r: '24h' | '7d' | '30d' | '90d' | 'all') => boolean;
  onLockedRange: (r: '24h' | '7d' | '30d' | '90d' | 'all') => void;
  orgs: { id: string; name: string; slug: string }[];
  depts: { id: string; name: string; organisation_id: string }[];
  users: { id: string; email: string; organisation_id: string }[];
  cards: { id: string; slug: string; user_id: string; short_code: string | null; data: { name?: string; firstName?: string; lastName?: string } }[];
  currentUserId: string | undefined;
}) {
  const { t } = useAppTranslation();
  const { scope, onScopeChange, range, onRangeChange, isRangeAllowed, onLockedRange, orgs, depts, users, cards, currentUserId } = props;
  const [filter, setFilter] = useState('');

  return (
    <div className="space-y-3">
      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1 rounded-xl bg-panel border border-line p-1">
        <ScopeTab
          active={scope?.kind === 'organisation'}
          onClick={() => {
            // For superadmin with multiple orgs, leave id null so we show the browser
            const target = orgs.length === 1 ? orgs[0]! : null;
            onScopeChange({ kind: 'organisation', id: target?.id ?? null, label: target?.name ?? 'Choose organisation' });
          }}
          icon={<Building2 className="h-4 w-4" />}
          label={t('analytics.scope.org', 'Organisation')}
        />
        <ScopeTab
          active={scope?.kind === 'department'}
          onClick={() => onScopeChange({ kind: 'department', id: null, label: t('analytics.chooseDepartment', 'Choose department') })}
          icon={<FolderTree className="h-4 w-4" />}
          label={t('analytics.scope.department', 'Department')}
          disabled={depts.length === 0}
        />
        <ScopeTab
          active={scope?.kind === 'user'}
          onClick={() => onScopeChange({ kind: 'user', id: null, label: t('analytics.chooseUser', 'Choose person') })}
          icon={<Users className="h-4 w-4" />}
          label={t('analytics.scope.user', 'Person')}
        />
        <ScopeTab
          active={scope?.kind === 'card'}
          onClick={() => onScopeChange({ kind: 'card', id: null, label: t('analytics.chooseCard', 'Choose card') })}
          icon={<CreditCard className="h-4 w-4" />}
          label={t('analytics.scope.card', 'Card')}
          disabled={cards.length === 0}
        />
        <div className="ml-auto" />
        {/* Range toggle */}
        <div className="flex items-center gap-1">
          {(['24h', '7d', '30d', '90d', 'all'] as const).map(r => {
            const allowed = isRangeAllowed(r);
            return (
              <button
                key={r}
                type="button"
                onClick={() => (allowed ? onRangeChange(r) : onLockedRange(r))}
                aria-disabled={!allowed}
                title={allowed ? undefined : 'Mit einem höheren Plan verfügbar'}
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                  range === r
                    ? 'bg-accent-600 text-white shadow'
                    : allowed ? 'text-ink-muted hover:text-ink' : 'text-ink-faint hover:text-ink-muted'
                }`}
              >
                {r}
                {!allowed && <Lock className="h-3 w-3" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* When an id is chosen, show a small "back" hint + a back-to-list button */}
      {scope && scope.id && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onScopeChange({ ...scope, id: null, label: '' })}
            className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink"
          >
            <ArrowLeft className="h-3 w-3" />
            {t('analytics.backToList', 'Back to list')}
          </button>
          <span className="text-sm font-medium text-ink">{scope.label}</span>
        </div>
      )}
    </div>
  );
}

function ScopeTab({ active, onClick, icon, label, disabled }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
        active
          ? 'bg-panel text-ink shadow-sm'
          : 'text-ink-muted hover:text-ink'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      {icon}
      {label}
    </button>
  );
}

function cardLabel(c: { slug: string; data: { name?: string; firstName?: string; lastName?: string }; short_code: string | null }): string {
  const name = c.data.name || `${c.data.firstName ?? ''} ${c.data.lastName ?? ''}`.trim() || c.slug;
  return c.short_code ? `${name} (${c.short_code})` : name;
}


// ─── Interactions: what visitors did on the card ─────────────────────────────
function InteractionsPanel({ stats }: { stats: Stats }) {
  const { t } = useAppTranslation();
  const rows = stats.events?.byType ?? [];
  const labels: Record<string, string> = {
    save_contact: t('analytics.ev.save_contact', 'Kontakt gespeichert'),
    click_email: t('analytics.ev.click_email', 'E-Mail angeklickt'),
    click_phone: t('analytics.ev.click_phone', 'Anruf angeklickt'),
    click_route: t('analytics.ev.click_route', 'Route geöffnet'),
    click_link: t('analytics.ev.click_link', 'Link geklickt'),
    click_social: t('analytics.ev.click_social', 'Web oder Social geklickt'),
    send_whatsapp: t('analytics.ev.send_whatsapp', 'Kontakt per WhatsApp aufgenommen'),
    send_email: t('analytics.ev.send_email', 'Kontakt per E-Mail aufgenommen'),
    send_call: t('analytics.ev.send_call', 'Rückruf angefragt'),
    show_qr: t('analytics.ev.show_qr', 'QR-Code geöffnet'),
  };
  const max = Math.max(1, ...rows.map((r) => r.count));
  const saves = rows.find((r) => r.type === 'save_contact');
  const visitors = stats.summary.uniqueSessions;
  const topTargets = (stats.events?.targets ?? []).slice(0, 8);

  return (
    <div className="rounded-xl bg-panel border border-line p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">{t('analytics.interactions', 'Interaktionen')}</h3>
        {saves && visitors > 0 && (
          <p className="text-xs text-ink-muted tabular-nums">
            {t('analytics.saveRate', 'Kontakt gespeichert: {{saves}} von {{visitors}} Besuchern ({{pct}} %)', {
              saves: saves.sessions, visitors, pct: Math.round((saves.sessions / visitors) * 100),
            })}
          </p>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {t('analytics.interactionsEmpty', 'Noch keine Interaktionen in diesem Zeitraum. Hier siehst du, wer deinen Kontakt speichert und welche Buttons und Links angeklickt werden.')}
        </p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <ul className="space-y-2.5">
            {rows.map((r) => (
              <li key={r.type}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-ink">{labels[r.type] ?? r.type}</span>
                  <span className="tabular-nums text-ink-muted">{r.count}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-raised">
                  <div className="h-full rounded-full bg-accent-500" style={{ width: `${(r.count / max) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
          {topTargets.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-ink">{t('analytics.topClicked', 'Meistgeklickt')}</p>
              <ul className="divide-y divide-line">
                {topTargets.map((r) => (
                  <li key={`${r.type}:${r.target}`} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                    <span className="truncate text-ink">{r.target}</span>
                    <span className="tabular-nums text-ink-muted">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Summary cards ───────────────────────────────────────────────────────────
function SummaryCards({ stats }: { stats: Stats }) {
  const { t } = useAppTranslation();
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label={t('analytics.totalViews', 'Total views')} value={stats.summary.totalViews} icon={Eye} />
      <Stat label={t('analytics.uniqueVisitors', 'Unique visitors')} value={stats.summary.uniqueSessions} icon={Users} />
      <Stat label={t('analytics.uniqueCards', 'Active cards')} value={stats.summary.uniqueCards} icon={CreditCard} />
      <Stat
        label={t('analytics.engagement', 'Engagement')}
        value={
          stats.summary.totalViews > 0
            ? `${Math.round((stats.summary.uniqueSessions / stats.summary.totalViews) * 100)}%`
            : '—'
        }
        icon={TrendingUp}
      />
    </div>
  );
}

function Stat({ label, value, icon: Icon }: { label: string; value: number | string; icon: LucideIcon }) {
  return (
    <div className="rounded-xl bg-panel border border-line p-4">
      <div className="flex items-center justify-between text-xs text-ink-muted">
        <span>{label}</span>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-2 text-2xl font-black text-ink tabular-nums">{value}</div>
    </div>
  );
}

// ─── Daily chart (pure SVG — no chart lib needed) ────────────────────────────
function DailyChart({ daily }: { daily: DailyPoint[] }) {
  const { t } = useAppTranslation();
  const W = 800;
  const H = 200;
  const PAD = 28;
  const max = Math.max(1, ...daily.map(d => d.views));
  const barW = daily.length > 0 ? Math.max(2, (W - PAD * 2) / daily.length - 2) : 4;

  return (
    <div className="rounded-xl bg-panel border border-line p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">
          {t('analytics.dailyTitle', 'Daily views')}
        </h3>
        <div className="flex items-center gap-3 text-xs text-ink-muted">
          <Legend color="bg-accent-500" label={t('analytics.legend.views', 'Views')} />
          <Legend color="bg-accent-400" label={t('analytics.legend.unique', 'Unique')} />
        </div>
      </div>
      {daily.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-sm text-ink-muted">
          {t('analytics.noData', 'No data yet — share a card to see views here.')}
        </div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-48">
          {/* Y gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map(t => {
            const y = PAD + (H - PAD * 2) * (1 - t);
            return (
              <g key={t}>
                <line x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="currentColor" strokeOpacity="0.1" />
                <text x={PAD - 4} y={y + 3} textAnchor="end" className="fill-current" style={{ fontSize: 9, color: 'rgb(148 163 184)' }}>
                  {Math.round(max * t)}
                </text>
              </g>
            );
          })}
          {daily.map((d, i) => {
            const x = PAD + i * ((W - PAD * 2) / daily.length);
            const hV = ((H - PAD * 2) * d.views) / max;
            const hU = ((H - PAD * 2) * d.unique) / max;
            return (
              <g key={d.day}>
                <rect
                  x={x} y={H - PAD - hV} width={barW} height={hV}
                  className="fill-accent-500"
                  rx="2"
                >
                  <title>{`${d.day}: ${d.views} views (${d.unique} unique)`}</title>
                </rect>
                <rect
                  x={x} y={H - PAD - hU} width={barW} height={hU}
                  className="fill-accent-400"
                  rx="2"
                  fillOpacity="0.7"
                />
              </g>
            );
          })}
          {/* X axis labels (first / mid / last) */}
          {[0, Math.floor(daily.length / 2), daily.length - 1].filter((v, i, a) => a.indexOf(v) === i).map(i => {
            const d = daily[i];
            if (!d) return null;
            const x = PAD + i * ((W - PAD * 2) / daily.length) + barW / 2;
            return (
              <text key={d.day} x={x} y={H - 6} textAnchor="middle" className="fill-current" style={{ fontSize: 9, color: 'rgb(148 163 184)' }}>
                {d.day.slice(5)}
              </text>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}

// ─── Top-list reusable ───────────────────────────────────────────────────────
function TopList({ title, icon: Icon, items, empty }: {
  title: string; icon: LucideIcon;
  items: { key: string; label: string; value: number }[];
  empty: string;
}) {
  const { t } = useAppTranslation();
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div className="rounded-xl bg-panel border border-line p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-ink-muted" />{title}
      </h3>
      {items.length === 0 ? (
        <div className="py-6 text-center text-sm text-ink-muted">{empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, 8).map(item => (
            <li key={item.key} className="flex items-center gap-2 text-sm">
              <span className="w-32 truncate text-ink" title={item.label}>{item.label}</span>
              <div className="flex-1 h-1.5 rounded-full bg-panel overflow-hidden">
                <div className="h-full bg-accent-500" style={{ width: `${(item.value / max) * 100}%` }} />
              </div>
              <span className="w-10 text-right tabular-nums text-xs text-ink-muted">
                {item.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Sublink management panel ────────────────────────────────────────────────
function SublinkPanel({ cardId, links, onChange }: { cardId: string; links: Sublink[]; onChange: () => void }) {
  const { t } = useAppTranslation();
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  async function create() {
    setError(null);
    const res = await fetch(`/api/analytics/links/${cardId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setError(String((body as { error?: string }).error || `HTTP ${res.status}`));
      return;
    }
    setLabel('');
    setCreating(false);
    onChange();
  }

  async function archive(linkId: string) {
    if (!confirm(t('analytics.confirmArchive', 'Archive this tracking link? Existing views stay in analytics.'))) return;
    const res = await fetch(`/api/analytics/links/${linkId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.ok) onChange();
  }

  function copy(slug: string) {
    const url = sublinkUrl(slug);
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(slug);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div className="rounded-xl bg-panel border border-line p-4">
      <header className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-ink inline-flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            {t('analytics.sublinksTitle', 'Tracking links')}
          </h3>
          <p className="text-xs text-ink-muted">
            {t('analytics.sublinksSubtitle', 'Create a separate link per event (trade show, LinkedIn post, etc.) and compare performance.')}
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent-600 hover:bg-accent-700 text-white px-3 py-1.5 text-sm font-medium transition"
          >
            <Plus className="h-4 w-4" />
            {t('analytics.newLink', 'New link')}
          </button>
        )}
      </header>

      {creating && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-raised border border-line p-3">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('analytics.linkLabelPlaceholder', 'e.g. Trade Show Berlin 2026')}
            maxLength={80}
            autoFocus
            className="flex-1 rounded-md bg-panel border border-line px-3 py-1.5 text-sm text-ink focus:outline-none focus:border-accent-500/50"
          />
          <button
            type="button"
            onClick={create}
            disabled={!label.trim()}
            className="rounded-md bg-accent-600 hover:bg-accent-700 text-white px-3 py-1.5 text-sm font-medium transition disabled:opacity-50"
          >
            {t('analytics.create', 'Create')}
          </button>
          <button
            type="button"
            onClick={() => { setCreating(false); setLabel(''); setError(null); }}
            className="rounded-md text-ink-muted hover:text-ink px-3 py-1.5 text-sm transition"
          >
            {t('analytics.cancel', 'Cancel')}
          </button>
        </div>
      )}
      {error && (
        <div className="mb-3 rounded-md border border-danger-300 dark:border-danger-500/40 bg-danger-50 dark:bg-danger-500/10 px-3 py-2 text-xs text-danger-700 dark:text-danger-200">
          {error}
        </div>
      )}

      {links.length === 0 ? (
        <div className="py-8 text-center text-sm text-ink-muted">
          {t('analytics.noLinks', 'No tracking links yet. Create one to measure specific channels.')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink-muted border-b border-line">
                <th className="py-2 pr-3 font-medium">{t('analytics.colLabel', 'Label')}</th>
                <th className="py-2 pr-3 font-medium">{t('analytics.colLink', 'Link')}</th>
                <th className="py-2 pr-3 font-medium text-right">{t('analytics.colViews', 'Views')}</th>
                <th className="py-2 font-medium text-right">{t('analytics.colActions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {links.map(l => (
                <tr key={l.id} className="border-b border-line last:border-0">
                  <td className="py-2.5 pr-3 text-ink">{l.label}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-ink-muted">
                    /t/{l.slug}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-ink">
                    {Number(l.views)}
                  </td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => copy(l.slug)}
                        title={t('analytics.copy', 'Copy link')}
                        className="rounded-md p-1.5 text-ink-muted hover:text-ink hover:bg-raised"
                      >
                        {copied === l.slug ? <Check className="h-3.5 w-3.5 text-success-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                      <a
                        href={sublinkUrl(l.slug)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('analytics.open', 'Open')}
                        className="rounded-md p-1.5 text-ink-muted hover:text-ink hover:bg-raised"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => archive(l.id)}
                        title={t('analytics.archive', 'Archive')}
                        className="rounded-md p-1.5 text-ink-muted hover:text-danger-500 dark:hover:text-danger-400 hover:bg-raised"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Scope browser — klickbare Liste/Grid zum Auswählen ──────────────────────
function ScopeBrowser({ scope, orgs, depts, users, cards, onPick }: {
  scope: Scope;
  orgs: { id: string; name: string; slug: string }[];
  depts: { id: string; name: string; organisation_id: string }[];
  users: { id: string; email: string; organisation_id: string }[];
  cards: { id: string; slug: string; user_id: string; short_code: string | null; data: { name?: string; firstName?: string; lastName?: string } }[];
  onPick: (s: Scope) => void;
}) {
  const { t } = useAppTranslation();
  const [filter, setFilter] = useState('');

  let items: Array<{ id: string; label: string; sub: string; icon: LucideIcon; onClick: () => void }> = [];
  let emptyHint = '';

  if (scope.kind === 'organisation') {
    emptyHint = t('analytics.noOrgs', 'No organisations available.');
    items = orgs.map(o => ({
      id: o.id,
      label: o.name,
      sub: o.slug ? `/${o.slug}` : '',
      icon: Landmark,
      onClick: () => onPick({ kind: 'organisation', id: o.id, label: o.name }),
    }));
  } else if (scope.kind === 'department') {
    emptyHint = t('analytics.noDepts', 'No departments in this organisation yet.');
    items = depts.map(d => ({
      id: d.id,
      label: d.name,
      sub: '',
      icon: Building2,
      onClick: () => onPick({ kind: 'department', id: d.id, label: d.name }),
    }));
  } else if (scope.kind === 'user') {
    emptyHint = t('analytics.noUsers', 'No other people in this organisation yet.');
    items = users.map(u => ({
      id: u.id,
      label: u.email,
      sub: '',
      icon: User,
      onClick: () => onPick({ kind: 'user', id: u.id, label: u.email }),
    }));
  } else if (scope.kind === 'card') {
    emptyHint = t('analytics.noCards', 'No cards in this organisation yet.');
    items = cards.map(c => ({
      id: c.id,
      label: cardLabel(c),
      sub: c.short_code ? `Code: ${c.short_code}` : '',
      icon: CreditCard,
      onClick: () => onPick({ kind: 'card', id: c.id, label: cardLabel(c) }),
    }));
  }

  const filtered = items.filter(i =>
    !filter || i.label.toLowerCase().includes(filter.toLowerCase()) || i.sub.toLowerCase().includes(filter.toLowerCase())
  );

  const titleMap: Record<Scope['kind'], string> = {
    organisation: t('analytics.pickOrg', 'Pick an organisation'),
    department:   t('analytics.pickDept', 'Pick a department'),
    user:         t('analytics.pickUser', 'Pick a person'),
    card:         t('analytics.pickCard', 'Pick a card'),
  };

  return (
    <div className="rounded-xl bg-panel border border-line p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">
          {titleMap[scope.kind]}
        </h3>
        {items.length > 6 && (
          <input
            type="search"
            placeholder={t('analytics.filter', 'Filter…')}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-md bg-panel border border-line px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50"
          />
        )}
      </div>
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-ink-muted">
          {emptyHint}
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {filtered.map(item => (
            <li key={item.id}>
              <button
                type="button"
                onClick={item.onClick}
                className="group flex w-full items-center gap-3 rounded-lg border border-line bg-raised px-3 py-3 text-left transition hover:border-accent-500/50 hover:bg-accent-50/50 dark:hover:bg-accent-500/10 hover:shadow-sm"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-panel ring-1 ring-line">
                  <item.icon className="h-4 w-4 text-ink-muted" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-ink">
                    {item.label}
                  </div>
                  {item.sub && (
                    <div className="truncate text-xs text-ink-muted font-mono">
                      {item.sub}
                    </div>
                  )}
                </div>
                <span className="text-ink-faint group-hover:text-accent-500 dark:group-hover:text-accent-400">
                  →
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Live badge ──────────────────────────────────────────────────────────────
function LiveBadge({ count }: { count: number }) {
  const { t } = useAppTranslation();
  if (count === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-panel px-2.5 py-1 text-xs text-ink-muted">
        <span className="h-1.5 w-1.5 rounded-full bg-ink-faint" />
        {t('analytics.noOneViewing', 'No one right now')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-success-500/30 bg-success-50 dark:bg-success-500/10 px-2.5 py-1 text-xs font-medium text-success-700 dark:text-success-300">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-success-500" />
      </span>
      <Radio className="h-3 w-3" />
      {t('analytics.viewingNow', '{count} viewing now').replace('{count}', String(count))}
    </span>
  );
}

// ─── Heatmap (day-of-week x hour) ────────────────────────────────────────────
function Heatmap({ points }: { points: HeatmapPoint[] }) {
  const { t } = useAppTranslation();
  // Build a 7x24 grid, indexed [dow][hour]
  const grid: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));
  for (const p of points) {
    if (p.dow >= 0 && p.dow < 7 && p.hour >= 0 && p.hour < 24) {
      grid[p.dow]![p.hour] = p.views;
    }
  }
  const max = Math.max(1, ...grid.flat());
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const cellSize = 16;
  const cellGap = 2;
  const labelW = 28;
  const labelH = 16;
  const W = labelW + 24 * (cellSize + cellGap);
  const H = labelH + 7 * (cellSize + cellGap);

  return (
    <div className="rounded-xl bg-panel border border-line p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">
          {t('analytics.heatmapTitle', 'When do people open your card?')}
        </h3>
        <div className="text-xs text-ink-muted">
          {t('analytics.heatmapHint', 'Darker = more views. Use it to time your outreach.')}
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="min-w-full" style={{ height: H + 20 }}>
          {/* Hour labels (top) */}
          {[0, 6, 12, 18, 23].map(h => (
            <text key={h} x={labelW + h * (cellSize + cellGap) + cellSize / 2} y={labelH - 4} textAnchor="middle" className="fill-current" style={{ fontSize: 9, color: 'rgb(148 163 184)' }}>
              {String(h).padStart(2, '0')}
            </text>
          ))}
          {grid.map((row, dow) => (
            <g key={dow}>
              <text x={0} y={labelH + dow * (cellSize + cellGap) + cellSize - 3} className="fill-current" style={{ fontSize: 10, color: 'rgb(148 163 184)' }}>
                {dayLabels[dow]}
              </text>
              {row.map((views, hour) => {
                const intensity = views / max;
                const x = labelW + hour * (cellSize + cellGap);
                const y = labelH + dow * (cellSize + cellGap);
                return (
                  <rect
                    key={hour}
                    x={x} y={y} width={cellSize} height={cellSize}
                    rx="3"
                    fill={`rgba(139, 92, 246, ${0.08 + intensity * 0.85})`}
                  >
                    <title>{`${dayLabels[dow]} ${String(hour).padStart(2, '0')}:00 — ${views} views`}</title>
                  </rect>
                );
              })}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

// ─── Loading skeleton ────────────────────────────────────────────────────────
function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="h-24 rounded-xl bg-panel animate-pulse" />
      ))}
    </div>
  );
}

// ─── Tiny ISO-country → flag/name map (expandable) ───────────────────────────
function countryName(code: string | null): string {
  if (!code) return 'Unknown';
  const map: Record<string, string> = {
    DE: 'Germany', AT: 'Austria', CH: 'Switzerland',
    US: 'United States', GB: 'United Kingdom', FR: 'France',
    ES: 'Spain', IT: 'Italy', NL: 'Netherlands',
    PL: 'Poland', BR: 'Brazil', IN: 'India', CN: 'China',
  };
  return map[code.toUpperCase()] || code;
}


/** Analytics is a Business feature: other plans see the upgrade prompt instead of the page. */
export default function Analytics() {
  const { t } = useAppTranslation();
  const { hasFeature, loading } = useTier();
  if (loading) return <Layout title={t('analytics.pageTitle', 'Analytics')}><div /></Layout>;
  if (!hasFeature('analytics')) {
    return (
      <Layout title={t('analytics.pageTitle', 'Analytics')}>
        <div className="max-w-xl p-4 sm:p-6 lg:p-8">
          <UpgradePrompt requiredTier="business" message={t('paywall.analytics', 'Analytics zeigt dir, wie oft und woher deine Karten aufgerufen werden. Verfügbar im Plan Business.')} />
        </div>
      </Layout>
    );
  }
  return <AnalyticsPage />;
}
